import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { getOpenCloseStatus, getNormalOpenCloseStatusForDay } from '@grandvisionhq/locations';
import { useIntl } from '@grandvisionhq/state';

const openNowFilterFunction = (store)=>{
    const { openingHours , specialOpeningHours  } = store;
    return [
        'OPEN',
        'CLOSING'
    ].includes(getOpenCloseStatus(openingHours, specialOpeningHours));
};
const openOnSundayFilterFunction = (store)=>{
    const { openingHours  } = store;
    const Sunday = 0;
    return [
        'OPEN',
        'CLOSING'
    ].includes(getNormalOpenCloseStatusForDay(openingHours, Sunday));
};
const openOnTheWeekendFilterFunction = (store)=>store.weekendOpen === true
;
const wheelChairAccessibleFilterFunction = (store)=>store.wheelchairAccessible === true
;
const useDefaultStoreSearchFilters = (additionalFilters = {})=>{
    const { getLabel  } = useIntl();
    const storeSearchFilters = React.useMemo(()=>swcHelpers.objectSpread({
            openNow: {
                label: getLabel('stores.component.storeFinder.filter.openNow'),
                active: false,
                filterFunction: openNowFilterFunction,
                order: 0
            },
            openOnTheWeekend: {
                label: getLabel('stores.component.storeFinder.filter.openOnTheWeekend'),
                active: false,
                filterFunction: openOnTheWeekendFilterFunction,
                order: 1
            },
            openOnSunday: {
                label: getLabel('stores.component.storeFinder.filter.openOnSunday'),
                active: false,
                filterFunction: openOnSundayFilterFunction,
                order: 2
            },
            wheelChairAccessible: {
                label: getLabel('stores.component.storeFinder.filter.wheelChairAccessible'),
                active: false,
                filterFunction: wheelChairAccessibleFilterFunction,
                order: 3
            }
        }, additionalFilters)
    , [
        getLabel,
        additionalFilters
    ]);
    return storeSearchFilters;
};
const setLoadingState = (callback)=>{
    callback(true);
    setTimeout(()=>callback(false)
    , 100);
};
const emptyObject = {};
const useStoreSearchFilters = (props)=>{
    const [loading, setLoading] = React.useState(false);
    var _filters1;
    const [storeSearchFilters, setStoreSearchFilters] = React.useState((_filters1 = props.filters) !== null && _filters1 !== void 0 ? _filters1 : emptyObject);
    const storeSearchFilterList = React.useMemo(()=>{
        const entries = Object.entries(storeSearchFilters);
        var _order, _order1;
        return entries.sort(([, a], [, b])=>((_order = a.order) !== null && _order !== void 0 ? _order : entries.length) - ((_order1 = b.order) !== null && _order1 !== void 0 ? _order1 : entries.length)
        ) // Unordered filters come last
        ;
    }, [
        storeSearchFilters
    ]);
    const hasActiveFilters = React.useMemo(()=>storeSearchFilterList.some(([, filter])=>filter.active
        )
    , [
        storeSearchFilterList
    ]);
    const setFilterActivity = React.useCallback((storeFilterName, active)=>{
        setLoadingState(setLoading);
        const filter = storeSearchFilters[storeFilterName];
        setStoreSearchFilters(swcHelpers.objectSpread({}, storeSearchFilters, {
            [storeFilterName]: swcHelpers.objectSpread({}, filter, {
                active
            })
        }));
    }, [
        storeSearchFilters
    ]);
    const activeFilters = React.useMemo(()=>{
        return Object.keys(storeSearchFilters).filter((key)=>{
            var ref;
            return ((ref = storeSearchFilters[key]) === null || ref === void 0 ? void 0 : ref.active) === true;
        });
    }, [
        storeSearchFilters
    ]);
    const clearAllFilters = React.useCallback(()=>{
        setLoadingState(setLoading);
        setStoreSearchFilters(Object.keys(storeSearchFilters).reduce((acc, key)=>{
            acc[key] = swcHelpers.objectSpread({}, storeSearchFilters[key], {
                active: false
            });
            return acc;
        }, swcHelpers.objectSpread({}, storeSearchFilters)));
    }, [
        storeSearchFilters
    ]);
    const applyActiveFilters = React.useCallback((storeSearchResults)=>{
        if (!hasActiveFilters) {
            return storeSearchResults;
        }
        return Object.values(storeSearchFilters).reduce((acc, filter)=>{
            if (!filter.active) {
                return acc;
            }
            return acc.filter(filter.filterFunction);
        }, storeSearchResults);
    }, [
        hasActiveFilters,
        storeSearchFilters
    ]);
    React.useEffect(()=>{
        var _filters;
        setStoreSearchFilters((_filters = props.filters) !== null && _filters !== void 0 ? _filters : emptyObject);
    }, [
        props.filters
    ]);
    return React.useMemo(()=>[
            {
                setFilterActivity,
                applyActiveFilters,
                clearAllFilters
            },
            {
                storeSearchFilters,
                storeSearchFilterList,
                hasActiveFilters,
                loading,
                activeFilters
            }, 
        ]
    , [
        setFilterActivity,
        applyActiveFilters,
        clearAllFilters,
        loading,
        storeSearchFilters,
        storeSearchFilterList,
        hasActiveFilters,
        activeFilters, 
    ]);
};

export { useStoreSearchFilters as default, useDefaultStoreSearchFilters, useStoreSearchFilters };
//# sourceMappingURL=use-store-search-filters.js.map
